// src/components/Hero.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome, // Kept for potential future use, not used in current badges
  faPhoneAlt,
  faShieldAlt as faShield, // Renamed for clarity if used in this context
  faClock,
  faMapMarkerAlt, // Kept for potential future use
  faArrowRight,
  faCalendarAlt,
  faTools, // Kept for potential future use
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import './Hero.css';

// API URL - should match your backend setup
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/contact';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    address: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState('');
  const [statusType, setStatusType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  // Animated gradient background colors
  const gradientColors = [
    'linear-gradient(135deg, #1D3557 0%, #2A4A7F 100%)',
    'linear-gradient(135deg, #1D3557 0%, #263E63 100%)',
    'linear-gradient(135deg, #223C66 0%, #1D3557 100%)'
  ];

  // Change background gradient every 7 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % gradientColors.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [gradientColors.length]); // Added dependency

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else if (!/^[0-9\s\+\-\(\)]{7,15}$/.test(formData.phone.trim())) { // Adjusted min length for phone
      errors.phone = 'Valid phone number is required';
      isValid = false;
    }

    if (!formData.service || formData.service === '') {
      errors.service = 'Please select a service';
      isValid = false;
    }

    if (!formData.address.trim()) {
      errors.address = 'Property address is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setFormStatus('Sending your request...');
    setStatusType('pending');

    try {
      const response = await fetch(`${API_URL}/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormStatus('Your quote request has been sent! We will contact you shortly.');
        setStatusType('success');
        setFormData({ name: '', phone: '', service: '', address: '' });

        setTimeout(() => {
          setFormStatus('');
          setStatusType('');
        }, 5000);
      } else {
        throw new Error(data.msg || 'Failed to send quote request');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('Error sending request. Please try again or call us directly.');
      setStatusType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="hero" style={{
      background: gradientColors[currentSlide],
    }}>
      <div className="hero__overlay-pattern"></div> {/* Renamed for clarity from hero__overlay */}
      <div className="hero__overlay-darken"></div> {/* New overlay for better text contrast */}

      <div className="hero__content">
        <motion.div
          className="hero__text-content" // Renamed for clarity
          initial="hidden"
          animate="visible"
          variants={fadeInLeft}
        >
          <div className="hero__subtitle">
            Trusted Roofing Professionals in Glossop
          </div>

          <h1 className="hero__title">
            Expert <span>Roofing Solutions</span> For Your Property
          </h1>

          <motion.p
            className="hero__description"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
          >
            Comprehensive roofing services with exceptional craftsmanship and commitment to quality.
            Old Glossop Roofing: Your premier choice for reliable and durable roofing.
          </motion.p>

          <motion.div
            className="hero__inline-badges" // Renamed for clarity
            variants={staggerChildren}
            initial="hidden"
            animate="visible"
          >
            <motion.div className="hero__badge-item" variants={fadeInUp}> {/* Renamed */}
              <div className="hero__badge-icon">
                <FontAwesomeIcon icon={faShield} />
              </div>
              <span>Quality Guaranteed</span>
            </motion.div>

            <motion.div className="hero__badge-item" variants={fadeInUp}> {/* Renamed */}
              <div className="hero__badge-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <span>Fast & Reliable</span>
            </motion.div>
          </motion.div>

          <motion.div
            className="hero__action-buttons" // Renamed
            variants={staggerChildren}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={fadeInUp}>
              <Link to="/services" className="hero__btn hero__btn--primary">
                Explore Our Services <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <a href="tel:07735005695" className="hero__btn hero__btn--secondary">
                <FontAwesomeIcon icon={faPhoneAlt} /> Call Us Today
              </a>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero__form-container" // Renamed
          variants={fadeInRight}
          initial="hidden"
          animate="visible"
        >
          <div className="quote-form">
            <h3 className="quote-form__title">Request a Free Quote</h3>

            <form onSubmit={handleSubmit}>
              <div className={`form__group ${formErrors.name ? 'error' : ''}`}>
                <label className="form__label" htmlFor="hero-name">Your Name</label> {/* Added unique ID prefix */}
                <input
                  id="hero-name"
                  type="text"
                  name="name"
                  className="form__input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., John Smith"
                  disabled={isSubmitting}
                />
                {formErrors.name && <div className="form__error-message">{formErrors.name}</div>} {/* Renamed class */}
              </div>

              <div className={`form__group ${formErrors.phone ? 'error' : ''}`}>
                <label className="form__label" htmlFor="hero-phone">Phone Number</label> {/* Added unique ID prefix */}
                <input
                  id="hero-phone"
                  type="tel"
                  name="phone"
                  className="form__input"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g., 07123 456789"
                  disabled={isSubmitting}
                />
                {formErrors.phone && <div className="form__error-message">{formErrors.phone}</div>} {/* Renamed class */}
              </div>

              <div className={`form__group ${formErrors.service ? 'error' : ''}`}>
                <label className="form__label" htmlFor="hero-service">Service Required</label> {/* Added unique ID prefix */}
                <select
                  id="hero-service"
                  name="service"
                  className="form__select"
                  value={formData.service}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="" disabled>Select a service...</option>
                  <option value="New Roof Installation">New Roof Installation</option>
                  <option value="Roof Repairs">Roof Repairs</option>
                  <option value="Roof Maintenance">Roof Maintenance</option>
                  <option value="Guttering & Fascias">Guttering & Fascias</option>
                  <option value="Chimney Repairs">Chimney Repairs</option>
                  <option value="Flat Roofing">Flat Roofing</option>
                  <option value="Emergency Roofing">Emergency Roofing</option>
                  <option value="Other">Other</option>
                </select>
                {formErrors.service && <div className="form__error-message">{formErrors.service}</div>} {/* Renamed class */}
              </div>

              <div className={`form__group ${formErrors.address ? 'error' : ''}`}>
                <label className="form__label" htmlFor="hero-address">Property Address / Postcode</label> {/* Added unique ID prefix */}
                <input
                  id="hero-address"
                  type="text"
                  name="address"
                  className="form__input"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="e.g., 123 High St, Glossop / SK13 8PN"
                  disabled={isSubmitting}
                />
                {formErrors.address && <div className="form__error-message">{formErrors.address}</div>} {/* Renamed class */}
              </div>

              <motion.button
                type="submit"
                className="form__submit-button" // Renamed
                disabled={isSubmitting}
                whileHover={{ y: -3, boxShadow: "0 6px 15px rgba(230, 57, 70, 0.4)" }}
                whileTap={{ y: 1 }}
              >
                {isSubmitting ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCalendarAlt} className="submit-icon" />
                    Schedule My Free Quote
                  </>
                )}
              </motion.button>

              {formStatus && (
                <motion.div
                  className={`form__status-message ${statusType}`} // Renamed
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {statusType === 'success' ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="status-icon" />
                  ) : statusType === 'error' ? (
                    <FontAwesomeIcon icon={faExclamationCircle} className="status-icon" />
                  ) : null}
                  {formStatus}
                </motion.div>
              )}
            </form>

            <p className="form__footer-note"> {/* Renamed */}
              No-obligation quotation • We typically respond within 24 hours.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Removed the trust-badges section from the bottom of the hero as per request */}
      {/*
      <motion.div
        className="trust-badges-footer" // This was the section removed
        // ...
      >
      </motion.div>
      */}
    </div>
  );
};

export default Hero;